import { useState } from 'react';
import pdfIcon from '../../assets/icons/pdf-icon.png';
import docIcon from '../../assets/icons/doc-icon.png';
import xlsIcon from '../../assets/icons/xls-icon.png';
import zipIcon from '../../assets/icons/zip-icon.png';
import defaultIcon from '../../assets/icons/file-icon.png';

const ICONS_BY_EXT = {
  pdf: pdfIcon,
  doc: docIcon,
  docx: docIcon,
  xls: xlsIcon,
  xlsx: xlsIcon,
  zip: zipIcon,
  rar: zipIcon,
  '7z': zipIcon,
  tar: zipIcon,
};

const getIconForExtension = (ext) => ICONS_BY_EXT[ext] || defaultIcon;

/**
 * Single-file download button with built-in loading feedback.
 *
 * Wraps the existing async download handler with a busy state so the user gets
 * immediate visual feedback when they click — no more multi-clicking on long
 * downloads from e-licitatie.ro that can take 10-30 seconds.
 *
 * While downloading:
 *   - File icon -> spinner
 *   - Button label -> dimmed + "se descarca..." suffix
 *   - Click is ignored (re-entry protected)
 *   - Background tinted indigo so the row visibly "claims" itself
 */
export default function FileDownloadButton({ fileName, onDownload, className = '' }) {
  const [isDownloading, setIsDownloading] = useState(false);

  const cleanName = fileName || 'Document';
  const extension = cleanName.split('.').pop().toLowerCase();

  const handleClick = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await onDownload();
    } catch (err) {
      // Bubble silently to console — handler already shows the user-facing alert.
      console.error('[FileDownloadButton] Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className={`flex items-center gap-2 border rounded p-2 transition ${
        isDownloading
          ? 'border-indigo-300 bg-indigo-50'
          : 'border-slate-200 bg-slate-50 hover:shadow'
      } ${className}`}
    >
      {isDownloading ? (
        <div className="w-6 h-6 flex items-center justify-center shrink-0">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-500 border-t-transparent"></div>
        </div>
      ) : (
        <img
          src={getIconForExtension(extension)}
          alt={`${extension} icon`}
          className="w-6 h-6 shrink-0"
        />
      )}
      <button
        type="button"
        onClick={handleClick}
        disabled={isDownloading}
        title={isDownloading ? 'Se descarcă, te rugăm să aștepți...' : cleanName}
        className={`flex-1 min-w-0 text-left text-xs font-semibold truncate transition ${
          isDownloading
            ? 'text-indigo-500 cursor-wait'
            : 'text-indigo-600 hover:underline cursor-pointer'
        }`}
      >
        <span className="truncate">{cleanName}</span>
        {isDownloading && (
          <span className="ml-1 text-[10px] text-indigo-400 italic font-normal">
            se descarcă…
          </span>
        )}
      </button>
    </div>
  );
}
